import along from "@turf/along";
import bearing from "@turf/bearing";
import { lineString, point } from "@turf/helpers";
import length from "@turf/length";

/**
 * The camera heading for a leg at a given progress along it.
 *
 * This lives in its own file because two sequences need to agree on it exactly. PathSequence asks
 * for it every frame; MarkerSequence asks for the arriving leg at phase 1 and the departing leg at
 * phase 0, so a stop begins pointing exactly where the flight before it ended and ends pointing
 * exactly where the next one starts. When they each had their own version -- an 18% window here, a
 * 60% window and a chord blend there -- the boundaries jumped by up to 85 degrees.
 *
 * The shape of it: slide a fixed-width window along the route rather than looking from "here" to
 * "here + n", which collapses at the end of the leg and turns the bearing into noise (measured at
 * 122 degrees of swing in a single frame). Then blend back toward the leg's own straight line, so
 * the camera stays aware of the road without riding every bend.
 */
const WINDOW_FRACTION = 0.6;
const CHORD_BLEND = 0.65;

/** Interpolate a bearing the short way round; 350 to 10 is 20 degrees, not 340. */
export const interpolateBearing = (t: number, from: number, to: number) => {
	const delta = ((to - from + 540) % 360) - 180;
	return from + delta * t;
};

export const legHeadingAt = (coords: any, phase: number): number => {
	if (!Array.isArray(coords) || coords.length < 2) return 0;
	try {
		const line = lineString(coords);
		const total = length(line);
		if (!(total > 0)) return 0;
		const window = Math.max(total * WINDOW_FRACTION, 0.3);
		const start = Math.max(0, Math.min(total - window, total * Math.max(0, Math.min(1, phase))));
		const a = along(line, start, { units: "kilometers" }).geometry.coordinates;
		const b = along(line, Math.min(total, start + window), { units: "kilometers" }).geometry.coordinates;
		const road = bearing(point(a), point(b));
		const chord = bearing(point(coords[0]), point(coords[coords.length - 1]));
		return interpolateBearing(CHORD_BLEND, road, chord);
	} catch {
		return 0;
	}
};

/**
 * Interpolate an altitude the way zoom actually works: logarithmically.
 *
 * Every zoom level halves the altitude, so a linear walk from 8,500,000 m to 2,682 m -- the jump
 * between an intercontinental leg and a stop on a bar crawl, a factor of 3,169 on one test trip --
 * spends almost the whole transition in the far range and then drops off a cliff at the end. In log
 * space the perceived zoom rate is constant, which is what reads as smooth.
 */
export const interpolateAltitude = (t: number, from: number, to: number) => {
	const a = Math.log(Math.max(1, from));
	const b = Math.log(Math.max(1, to));
	return Math.exp(a + (b - a) * Math.max(0, Math.min(1, t)));
};
